import React, { useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  ConnectivityEmailListParams,
  useGetConnectivityEmailProcessing,
} from '~/hooks/apis/connectivity/useConnectivityEmailProcessing';

const PAGE_SIZE = 50;

const ACTION_TAKEN_LABELS: Record<string, string> = {
  extranet_reply_sent: 'Extranet reply sent',
  case_closed: 'Case closed',
  none: 'No action',
};

const STATUS_COLOURS: Record<string, string> = {
  completed: 'success.main',
  processing: 'warning.main',
  abandoned: 'text.disabled',
};

export default function ConnectivityEmailsPage() {
  const [caseIdInput, setCaseIdInput] = useState('');
  const [actionTakenInput, setActionTakenInput] = useState('');
  const [queryParams, setQueryParams] = useState<ConnectivityEmailListParams>({ page: 1, limit: PAGE_SIZE });

  const { data, isLoading, isError } = useGetConnectivityEmailProcessing(queryParams);

  const records = data?.result?.records ?? [];
  const total = data?.result?.total ?? 0;
  const page = queryParams.page ?? 1;

  const handleSearch = () => {
    setQueryParams({
      sf_case_id: caseIdInput.trim() || undefined,
      action_taken: actionTakenInput || undefined,
      page: 1,
      limit: PAGE_SIZE,
    });
  };

  const handleClear = () => {
    setCaseIdInput('');
    setActionTakenInput('');
    setQueryParams({ page: 1, limit: PAGE_SIZE });
  };

  return (
    <Box>
      <PageSubheader title="Connectivity Emails" />

      <Stack direction="row" spacing={2} alignItems="center" mt={2} mb={3} flexWrap="wrap">
        <TextField
          label="Case ID"
          placeholder="5002y000009TESTAA1"
          value={caseIdInput}
          onChange={(e) => setCaseIdInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Action taken</InputLabel>
          <Select label="Action taken" value={actionTakenInput} onChange={(e) => setActionTakenInput(e.target.value)}>
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {Object.entries(ACTION_TAKEN_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>

        <Button variant="text" onClick={handleClear}>
          Clear
        </Button>

        {isLoading && <CircularProgress size={20} />}
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load records.
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Case ID</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>From</TableCell>
              <TableCell>Classification</TableCell>
              <TableCell>Action taken</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No records found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {records.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {row.sf_case_id}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                  <Typography variant="body2" noWrap title={row.subject ?? undefined}>
                    {row.subject ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.from_address ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.classification ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  {row.action_taken ? (ACTION_TAKEN_LABELS[row.action_taken] ?? row.action_taken) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={STATUS_COLOURS[row.status] ?? 'text.primary'} fontWeight={500}>
                    {row.status}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {dayjs(row.created_at).format('D MMM YYYY HH:mm')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_, p) => setQueryParams((prev) => ({ ...prev, page: p + 1 }))}
      />
    </Box>
  );
}
